import { Link } from "react-router-dom";
import { PageHero } from "../components/PageShell";
import { Figure } from "../components/Figure";
import { RECRUITER_NAME_TO_SLUG, RECRUITMENT_PARTNERS } from "../lib/recruiters";
import { ROUTES } from "../lib/routes";
import { ContactBand } from "../components/ContactBand";
import { DeanBand } from "../components/DeanBand";
import { DEANS, deanPhoto } from "../lib/deans";
import { DEPARTMENTS } from "../lib/departments";
import {
  DRIVE_DAY,
  ELIGIBILITY,
  PLACEMENT_CONTACTS,
  PLACEMENT_VENUES_ALT,
  STUDENT_PROCESS,
} from "../lib/placementCell";
import {
  PLACEMENT_BATCHES,
  deptTotals,
  employerCount,
  summaryFigures,
} from "../lib/placements";
import { PlacementCtcBands } from "../components/sections/PlacementCtcBands";
import { PlacementBranchGrid } from "../components/sections/PlacementBranchGrid";
import { PlacementCompanyMix } from "../components/sections/PlacementCompanyMix";
import { latestPlacementYear } from "../lib/placementTrends";

/**
 * Two records feed this page, and they count different things.
 *
 * The headline and the five-year figures come from the Cell's year-wise
 * aggregate (placementTrends.ts), which states STUDENTS PLACED — 639 for the
 * 2026 batch.
 *
 * The recruiter wall and the department breakdown come from the Cell's
 * drive-by-drive sheet (placements.ts), whose "Selected" column counts
 * SELECTIONS: a student picked up by two companies appears in both rows. That
 * sheet caught 596 selections across 54 recorded drives — a partial capture of
 * a year that finished at 639 — so per-company figures still say "offers" and
 * the recruiter section states what it covers.
 *
 * What was here before either record arrived was invented end to end: "377
 * students placed", a "28.5 LPA highest package", a "22 LPA median", a
 * five-year growth trend, a quote from an unnamed dean and eight alumni called
 * "Alumnus 01" through "Alumnus 08". None of it is kept.
 */
const LATEST = PLACEMENT_BATCHES[0];
const PREVIOUS = PLACEMENT_BATCHES[1];
const YEAR = latestPlacementYear();

const TP_DEAN = DEANS.find((d) => d.role.includes("Training & Placement"));

/** Her published Cell contact, so the band can carry a phone and an address. */
const TP_CONTACT = PLACEMENT_CONTACTS.find((c) => c.name === TP_DEAN?.name);

/**
 * The roster gives her full title, "Dean Training & Placement, Dean IT Cell".
 * Only the Training & Placement half belongs on this page, so it is trimmed
 * here rather than edited out of deans.ts, where the full title is right for
 * the Deans roster.
 */
const TP_DEAN_ROLE = "Dean Training & Placement";

/** Same trim on the portrait's alt text, so a screen reader hears what is shown. */
const TP_DEAN_BASE_PHOTO = TP_DEAN ? deanPhoto(TP_DEAN) : undefined;
const TP_DEAN_PHOTO = TP_DEAN_BASE_PHOTO
  ? { ...TP_DEAN_BASE_PHOTO, alt: `${TP_DEAN!.name}, ${TP_DEAN_ROLE}` }
  : TP_DEAN_BASE_PHOTO;

/**
 * The six the Cell leads its partner list with, read off that list so the hero
 * cannot drift from the wall further down the page.
 */
const TOP_PARTNERS = RECRUITMENT_PARTNERS.slice(0, 6);
const TOP_PARTNERS_SENTENCE =
  TOP_PARTNERS.slice(0, -1).join(", ") + " and " + TOP_PARTNERS[TOP_PARTNERS.length - 1];
const DEPT_NAME = new Map(DEPARTMENTS.map((d) => [d.code, d.name]));

export default function Placements() {
  const figures = summaryFigures(LATEST);
  const prevFigures = summaryFigures(PREVIOUS);
  const byDept = deptTotals(LATEST);

  return (
    <>
      <PageHero
        variant="hero-data"
        eyebrow={`Placements · ${LATEST.batch} batch`}
        title={`${YEAR.placed} students placed from ${employerCount(LATEST)} companies.`}
        lead={`The Training & Placement Cell placed ${YEAR.placed} students from the ${LATEST.batch} graduating batch, with recruiters including ${TOP_PARTNERS_SENTENCE}.`}
        crumbs={[{ label: "Placements" }]}
        image={null}
        aside={
          <div className="hero-facts">
            <div className="hf-row">
              <span className="hf-num">{YEAR.placed}</span>
              <span className="hf-lbl">Students placed</span>
            </div>
            <div className="hf-row">
              <span className="hf-num">{employerCount(LATEST)}</span>
              <span className="hf-lbl">Companies</span>
            </div>
            <div className="hf-row">
              <span className="hf-num">{figures.highest} LPA</span>
              <span className="hf-lbl">Highest package</span>
            </div>
            <ul className="hf-jump">
              {/* Plain rows, not links: these pointed at #branches, and the
                  department breakdown section they jumped to has been removed.
                  The figures stay — they are that breakdown in miniature. */}
              {byDept.slice(0, 6).map((d) => (
                <li key={d.code}>
                  <span className="hf-jump-row">
                    {DEPT_NAME.get(d.code) ?? d.code} <span>{d.offers} offers</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        }
      />

      {/* 1 · The figures the Cell states itself */}
      <section className="section" style={{ paddingTop: "var(--space-7)", paddingBottom: 0 }}>
        <div className="container">
          <div className="plc-headline reveal">
            <div className="plc-headline-kicker">
              As recorded by the Placement Cell · {LATEST.batch} batch
            </div>
            {[
              { label: "Students placed", now: String(YEAR.placed), was: String(PREVIOUS.totalSelected) },
              { label: "Drives", now: String(LATEST.drives.length), was: String(PREVIOUS.drives.length) },
              { label: "Highest", now: figures.highest, was: prevFigures.highest, unit: "LPA" },
              // Not a batch-on-batch figure like its siblings — it's the five-year
              // cumulative count (FIG-PLC-005), so it gets its own honest caption
              // instead of a fabricated "was" comparison against last year.
              { label: "Recruitment partners", now: "180+", caption: "2022–2026 · cumulative" },
            ].map((f) => (
              <div key={f.label} className="plc-headline-cell">
                <div className="plc-headline-label">{f.label}</div>
                <div className="plc-headline-num">
                  {f.now}
                  {f.unit && <small>{f.unit}</small>}
                </div>
                <div className="plc-headline-prev">
                  {f.caption ?? `${PREVIOUS.batch} · ${f.was}${f.unit ? ` ${f.unit}` : ""}`}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2 · Where a drive happens. Alternative frames to the recruiter page's
             gallery, so the two do not show the same four photographs. */}
      <section className="section section-tight">
        <div className="container">
          <div className="venue-grid venue-grid--3">
            {PLACEMENT_VENUES_ALT.map((v) => (
              <figure key={v.media.url} className="venue-card reveal">
                <Figure
                  media={v.media}
                  fallbackLabel={v.title}
                  aspect="4/3"
                  sizes="(max-width: 700px) 92vw, (max-width: 1100px) 46vw, 400px"
                />
                <figcaption>
                  <span className="venue-title">{v.title}</span>
                  <span className="venue-caption">{v.caption}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* 3 · The five-year record — three independent sections, one per
             figure, rather than one section holding all three. */}
      <section className="section section-tight">
        <div className="container">
          <div className="kicker">The five-year record</div>
          <h2 className="plc-h2">Year-wise CTC Band Distribution</h2>
          <PlacementCtcBands />
        </div>
      </section>

      <section className="section section-tight">
        <div className="container">
          <h2 className="plc-h2">Branch-wise Placements</h2>
          <PlacementBranchGrid />
        </div>
      </section>

      <section className="section section-tight">
        <div className="container">
          <h2 className="plc-h2">Company-Type Distribution</h2>
          <PlacementCompanyMix />
        </div>
      </section>

      {/* 4 · Recruiters, with the offers each actually made */}
      <section className="section">
        <div className="container">
          <div className="kicker-row">
            <div className="kicker">Recruiters</div>
            <span className="plc-kicker-meta">
              {RECRUITMENT_PARTNERS.length} PARTNERS · {LATEST.batch} BATCH
            </span>
          </div>
          <h2 className="plc-h2">Recruitment Partners</h2>
          <div className="recruiter-wall reveal">
            {RECRUITMENT_PARTNERS.map((company) => {
              const slug = RECRUITER_NAME_TO_SLUG[company];
              const inner = <div>{company}</div>;
              return slug ? (
                <Link key={company} to={`${ROUTES.recruiters}/${slug}`} className="recruiter-cell">
                  {inner}
                </Link>
              ) : (
                <div key={company} className="recruiter-cell">
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5 · Recruiter connector */}
      <section className="section section-tight">
        <div className="container">
          <div className="recruiter-cta reveal">
            <div className="rcta-main">
              <div className="rcta-eyebrow">For recruiters</div>
              <h2>Hiring from BIET? Start a campus drive.</h2>
              <p>
                One cell end to end, drives running from autumn through to the following
                summer, and graduates across {byDept.length} departments. Pre-placement
                talks, on-campus assessment halls and a single point of contact.
              </p>
              <div className="rcta-actions">
                <Link to={ROUTES.recruiters} className="rcta-btn">
                  Open the recruiter portal <span aria-hidden="true">&rarr;</span>
                </Link>
              </div>
            </div>
            <div className="rcta-stats" aria-hidden="true">
              <div className="rcta-stat">
                <b>{YEAR.placed}</b>
                <span>Students placed · {LATEST.batch}</span>
              </div>
              <div className="rcta-stat">
                <b>180+</b>
                <span>Recruitment partners</span>
              </div>
              <div className="rcta-stat">
                <b>&#8377;{figures.highest}L</b>
                <span>Highest package</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6 · What a student has to do.
             Was two blocks that ran backwards — eligibility and the drive day
             first, then a heading "Before any of that" reaching back to the
             sixth semester. They are one chronology, so they are now one
             sequence, with eligibility alongside as the gate rather than as a
             step in it. */}
      <section className="section section-tight">
        <div className="container">
          <div className="kicker">For students</div>
          <h2 className="plc-h2">Getting into a drive.</h2>
          <div className="plc-two-col">
            <div>
              <ol className="plc-steps">
                {STUDENT_PROCESS.map((step) => (
                  <li key={step.title}>
                    <h3>{step.title}</h3>
                    <p>{step.body}</p>
                  </li>
                ))}
                <li>
                  <h3>The drive day</h3>
                  <ul className="plc-day">
                    {DRIVE_DAY.map((event) => (
                      <li key={event}>{event}</li>
                    ))}
                  </ul>
                </li>
              </ol>
            </div>
            <div className="plc-gate">
              <h3>Eligibility</h3>
              <ul className="lib-rules">
                {ELIGIBILITY.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 7 · The Cell — closing the two audience sections. A recruiter has just read
          "start a campus drive" and a student "getting into a drive"; both end at
          the person who runs them. */}
      {TP_DEAN && (
        <DeanBand
          kicker="Training &amp; Placement Cell"
          title="One cell runs every drive on campus."
          name={TP_DEAN.name}
          role={TP_DEAN_ROLE}
          photo={TP_DEAN_PHOTO}
          phone={TP_CONTACT?.phone}
          email={TP_CONTACT?.email}
        >
          The Cell schedules the drives, hosts recruiters on campus and keeps the
          record this page is built from — {LATEST.drives.length} drives for the{" "}
          {LATEST.batch} batch alone. Recruiters who want to hire here start with the
          Cell.
        </DeanBand>
      )}

      {/* 8 · Who to contact */}
      <ContactBand
        title="The Training & Placement Cell."
        entries={PLACEMENT_CONTACTS.map((c) => ({
          role: c.role,
          name: c.name,
          detail: c.detail,
          phone: c.phone,
          email: c.email,
        }))}
      />

    </>
  );
}
